import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';

import { useApiMutation } from 'theSource/utils';
import { POPUP_CONTENT_QUERY_KEY } from 'enrollment/features/ToDoList/utils';
import { PlanContentFile } from 'enrollment/features/Plans/PlanCard/components/PlanPopup/PlanContentFile';
import {
  PopupContentResponseDto,
  PopupContentRequestDto,
  PopupContentDto,
} from 'enrollment/features/ToDoList/types';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { ErrorResponseDto } from 'theSource/types';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

export const IndividualPlanInfoItem = ({
  orgBenefitId,
  coverage,
  lumosApiHost,
  lastPlan = false,
}: {
  orgBenefitId: number;
  coverage: CategoryCoverageDto;
  lumosApiHost: string;
  lastPlan?: boolean;
}) => {
  const { t } = useTranslation('enrollment.additionalPlanInfo');
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const [planInfoData, setPlanInfoData] = useState<PopupContentDto>();

  const { isLoading: isLoadingPopup, mutate: getPopupContent } = useApiMutation<
    PopupContentResponseDto,
    ErrorResponseDto,
    PopupContentRequestDto
  >({
    queryKey: POPUP_CONTENT_QUERY_KEY,
    method: 'post',
    endpoint: '/cart/coverages/popups',
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  useEffect(() => {
    if (orgBenefitId && planInfoData === undefined) {
      // Fetch the popup content
      getPopupContent(
        {
          org_benefit_id: orgBenefitId,
          life_event_completed: false,
          enrollment_context_type: enrollmentContext || '',
          election: {
            org_plan_id: coverage.org_plan_id,
            coverage_level_id: coverage.coverage_level_id,
            dependent_ids: [],
            subscriber_amount: coverage.subscriber_premium,
          },
        },
        {
          onSuccess: (data) => {
            setPlanInfoData(data.data);
          },
        }
      );
    }
  }, [
    orgBenefitId,
    planInfoData,
    enrollmentContext,
    coverage,
    getPopupContent,
  ]);

  if (isLoadingPopup || !planInfoData) {
    return (
      <Skeleton
        variant="rectangular"
        height={100}
        role="status"
        aria-label="Loading..."
        className="mb-3"
      />
    );
  }

  const popupContents = planInfoData?.plan_popup_contents || [];
  const popupFiles = planInfoData?.plan_popup_files || [];
  const planCarrierFiles = planInfoData?.carrier_files || [];
  const planContentFiles = planInfoData?.files || [];

  return (
    <div
      id={`planPopupContent-${coverage.org_plan_id}`}
      key={`planPopupContent-${coverage.org_plan_id}`}
      className={`mb-3 ${lastPlan ? '' : 'pb-3 border-bottom'}`}
    >
      <h3>{coverage.org_plan_name}:</h3>
      <div>
        {popupContents.map((content: string) => (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        ))}
      </div>
      {popupFiles.length + planContentFiles.length + planCarrierFiles.length >
        0 && (
        <div>
          <h4 className="mt-2 mb-0">{t('cardDownload')}:</h4>
          <div>
            {popupFiles.map((file, index) => (
              <div key={`${coverage.org_plan_id}-popupFiles-${index}`}>
                <PlanContentFile file={file} apiHost={lumosApiHost} />
              </div>
            ))}
            {planCarrierFiles.map((file, index) => (
              <div key={`${coverage.org_plan_id}-planCarrierFiles-${index}`}>
                <PlanContentFile file={file} apiHost={lumosApiHost} />
              </div>
            ))}
            {planContentFiles.map((file, index) => (
              <div key={`${coverage.org_plan_id}-planContentFiles-${index}`}>
                <PlanContentFile file={file} apiHost={lumosApiHost} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
